import { get, post, qs, request, API, getAccessToken } from './http.ts';
import type { ApiNode, ListResult, SortField, SortOrder } from './types.ts';

export const listFolder = (
  namespaceId: string, path: string, sort: SortField, order: SortOrder,
  cursor?: string, signal?: AbortSignal,
): Promise<ListResult> =>
  get<ListResult>(`/nodes${qs({ namespace_id: namespaceId, path, sort, order, limit: 500, cursor })}`,
    signal);

export const metadata = (namespaceId: string, path: string): Promise<ApiNode> =>
  get<ApiNode>(`/nodes/metadata${qs({ namespace_id: namespaceId, path })}`);

export const createFolder = (
  namespaceId: string, path: string, autorename = false,
): Promise<ApiNode> =>
  post<ApiNode>('/nodes/folder', { namespace_id: namespaceId, path, autorename });

export interface MoveItem { from: string; to: string }

export const moveNodes = (
  namespaceId: string, items: MoveItem[], autorename = false,
): Promise<{ results: Array<{ from: string; to: string; node: ApiNode }>; journal_seq: number }> =>
  post('/nodes/move', { namespace_id: namespaceId, items, autorename });

export const copyNodes = (
  namespaceId: string, items: MoveItem[], autorename = true,
): Promise<{ copied_items: number; journal_seq: number }> =>
  post('/nodes/copy', { namespace_id: namespaceId, items, autorename });

export const deleteNodes = (
  namespaceId: string, paths: string[],
): Promise<{ trash_ids: string[]; deleted_items: number; journal_seq: number }> =>
  post('/nodes/delete', { namespace_id: namespaceId, paths });

export const previewText = (
  namespaceId: string, path: string, versionId?: string,
): Promise<{ kind: string; content: string; truncated: boolean }> =>
  get(`/files/preview${qs({ namespace_id: namespaceId, path, version_id: versionId })}`);

const downloadToken = (
  body?: { namespace_id: string; path: string; version_id?: string },
): Promise<{ token: string }> =>
  request('/files/download-token', { method: 'POST', body: body ?? {} });

/** 액세스 토큰을 URL 에 노출하지 않도록 60초짜리 1회용 토큰을 받아 사용한다. */
export async function downloadUrl(
  namespaceId: string, path: string, versionId?: string,
): Promise<string> {
  const { token } = await downloadToken({
    namespace_id: namespaceId, path, ...(versionId ? { version_id: versionId } : {}),
  });
  return `${API}/files/download${qs({
    namespace_id: namespaceId, path, version_id: versionId, dl: token,
  })}`;
}

export async function zipUrl(
  namespaceId: string, paths: string[], name: string,
): Promise<string> {
  const { token } = await downloadToken();
  const search = new URLSearchParams({ namespace_id: namespaceId, name, dl: token });
  for (const p of paths) search.append('paths', p);
  return `${API}/files/zip?${search.toString()}`;
}

/** 미리보기용 blob URL. 인증 헤더가 필요하므로 fetch 로 받아 온다. */
export async function fetchBlobUrl(
  namespaceId: string, path: string, versionId?: string,
): Promise<string> {
  const url = `${API}/files/download${qs({
    namespace_id: namespaceId, path, version_id: versionId, inline: true,
  })}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
  });
  if (!res.ok) throw new Error('미리보기를 불러오지 못했습니다');
  return URL.createObjectURL(await res.blob());
}

// 썸네일은 작고 내용 주소 기반이라 한 번 받으면 계속 재사용한다.
// 목록을 오갈 때마다 다시 받지 않도록 모듈 수준에서 blob URL 을 캐시한다.
const thumbCache = new Map<string, string>();
const THUMB_CACHE_MAX = 300;

export type ThumbResult = string | 'generating';

export async function thumbnailBlobUrl(
  namespaceId: string, path: string, variant: 's' | 'm' | 'l' = 'm',
): Promise<ThumbResult> {
  const key = `${namespaceId}:${path}:${variant}`;
  const cached = thumbCache.get(key);
  if (cached) return cached;

  const url = `${API}/files/thumbnail${qs({ namespace_id: namespaceId, path, variant })}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
  });

  if (res.status === 202) return 'generating';
  if (!res.ok) throw new Error('썸네일을 불러오지 못했습니다');

  const objectUrl = URL.createObjectURL(await res.blob());
  if (thumbCache.size >= THUMB_CACHE_MAX) {
    const oldest = thumbCache.keys().next().value;
    if (oldest) {
      URL.revokeObjectURL(thumbCache.get(oldest)!);
      thumbCache.delete(oldest);
    }
  }
  thumbCache.set(key, objectUrl);
  return objectUrl;
}
